import { PostType } from "../RootPost";
import { App, SignInState } from "../app";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { DeflectInfo } from "../components/Post/Submission/SubmitPostView";

const fullEventName = (eventName: string): string => `VS/Feedback/ReportProblem/${eventName}`;
const fullPropName = (propName: string, isBIProp?: boolean): string => isBIProp ? `vs.bi.${propName}` : `VS.Feedback.${propName}`;
const fullBIPropName = (propName: string): string => fullPropName(propName, true);

export const clientVersion: string = "1.0.151";

class StringMap {
    [key: string]: any;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
class UserTaskEvent {
    eventName: string
    constructor(eventName: string, result: any, details: any, props: any) {
        this.eventName = eventName;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public start(...args: any): void {

    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public end(...args: any): void {

    }
}
export enum TelemetryResult {
    /**
     * Used for unknown or unavailable result.
     */
    None = 0,
    /**
     * A result without any failure from product or user.
     */
    Success = 1,
    /**
     * A result to indicate the action/operation failed because of product issue (not user faults)
     * Consider using FaultEvent to provide more details about the failure.
     */
    Failure = 2,
    /**
     * A result to indicate the action/operation failed because of user fault (e.g., invalid input).
     * Consider using FaultEvent to provide more details.
     */
    UserFault = 3,
    /**
     * A result to indicate the action/operation is cancelled by user.
     */
    UserCancel = 4,
}

type onUpdateCallback = (data: Map<string, string>) => void;

export class TelemetryHelper {
    private telemetry: ApplicationInsights;
    private feedbackSessionId: string;
    private sessionStartTime: string;

    private onUpdate: onUpdateCallback;

    constructor(appInsights: ApplicationInsights, onUpdate: onUpdateCallback) {
        this.telemetry = appInsights;
        this.onUpdate = onUpdate;
    }

    // @TODO: Handle optin after thinking through how it will work for standalone website
    public update(optin: boolean, product: string, build: string, sessionId: string): void {
        this.feedbackSessionId = sessionId || "unknown";
        this.sessionStartTime = (new Date()).toISOString();

        if (this.onUpdate) {
            const props = new Map<string, string>();

            props.set(fullPropName("serverName"), window.location.host);    // e.g. developercommunity.visualstudio.com or localhost
            props.set(fullPropName("ABExp.Flights"), App.settings.capabilities.expFlights);

            props.set(fullPropName("ClientName"), product || "unknown");
            props.set(fullPropName("ClientVersion"), build || "unknown");
            props.set(fullPropName("FeedbackSessionId"), this.feedbackSessionId);
            props.set(fullPropName("FeedbackSessionStartTime"), this.sessionStartTime);

            this.onUpdate(props);
        }
    }

    public startOperation(eventName: string, details?: string, feedbackId?: string, extraProperties?: { [key: string]: string }): { end: (result?: TelemetryResult, details?: string) => void } {
        this.debugLog("startOperation", eventName, null, details, feedbackId, extraProperties);

        this.telemetry.startTrackEvent(fullEventName(eventName));

        return {
            end: (result?: TelemetryResult, details?: string) => {
                const props = {
                    ...this.fillProperties(details, feedbackId, extraProperties),
                    "DataModel.eventType": "Operation",
                    // eslint-disable-next-line security/detect-object-injection
                    "DataModel.result": TelemetryResult[result]
                }

                this.telemetry.stopTrackEvent(fullEventName(eventName), props);
            }
        }
    }

    public startUserTask(eventName: string, details?: string, feedbackId?: string, extraProperties?: { [key: string]: string }): { end: (result?: TelemetryResult, details?: string) => void } {
        this.debugLog("startUserTask", eventName, null, details, feedbackId, extraProperties);

        this.telemetry.startTrackEvent(fullEventName(eventName));

        return {
            end: (result?: TelemetryResult, details?: string) => {
                const props = {
                    ...this.fillProperties(details, feedbackId, extraProperties),
                    "DataModel.eventType": "UserTask",
                    // eslint-disable-next-line security/detect-object-injection
                    "DataModel.result": TelemetryResult[result]
                }

                this.telemetry.stopTrackEvent(fullEventName(eventName), props);
            }
        }
    }

    public postUserTask(eventName: string, result: TelemetryResult, details?: string, feedbackId?: string, extraProperties?: { [key: string]: string }): void {
        this.debugLog("postUserTask", eventName, result, details, feedbackId, extraProperties);

        const props = {
            ...this.fillProperties(details, feedbackId, extraProperties, true),
            "DataModel.eventType": "UserTask",
            // eslint-disable-next-line security/detect-object-injection
            "result": TelemetryResult[result]
        }

        this.telemetry.trackEvent({ name: fullEventName(eventName) }, props);
    }

    /** Used for events that can capture deflection rate funnel.
     * Made as a separate method to make it easier to track (find all references) deflection rate.
     * @param info - context information about the deflection such as post type, deflection session ID
    */
    public deflect(info: DeflectInfo, partialEventName: string, extraProperties?: { [key: string]: string }, result: TelemetryResult = TelemetryResult.Success, details?: string): void {
        const newPostTypeString = PostType[info.newPostType] ?? info.newPostType.toString();
        this.postBusinessEvent(`deflect/${partialEventName}`, {
            "newPostType": newPostTypeString,
            "deflectSessionId": info.deflectSessionId,
            "experiments": info.experiments,
            ...(extraProperties ?? {})
        }, result, details);
    }

    public postBusinessEvent(eventName: string, extraProperties?: { [key: string]: string }, result: TelemetryResult = TelemetryResult.Success, details?: string): void {
        this.debugLog("➡️postBusinessEvent", eventName, result, details, undefined, extraProperties);

        const props = {
            ...this.fillProperties(details, undefined, extraProperties, true),
            "DataModel.eventType": "Event",
            "vs.isbi": "true",
            // eslint-disable-next-line security/detect-object-injection
            "DataModel.result": TelemetryResult[result]
        }

        this.telemetry.trackEvent({ name: fullEventName(eventName) }, props);
    }

    public postCriticalFault(eventName: string, description: string, error?: Error, feedbackId?: string, extraProperties?: { [key: string]: string }): void {
        const props = { ...(extraProperties ?? {}), "DataModel.severity": "Critical" };
        this.postFault(`ERR_CRIT_${eventName}`, description, error, feedbackId, props);
    }

    public postFault(eventName: string, description: string, error?: Error, feedbackId?: string, extraProperties?: { [key: string]: string }): void {
        this.debugLog("postFault", eventName, null, description, undefined, undefined);

        const props = {
            ...this.fillProperties(description, feedbackId, extraProperties, true),
            "DataModel.eventType": "Fault",
            "DataModel.error.name": error?.name,
            "DataModel.error.message": error?.message,
            "DataModel.error.stack": error?.stack,
        };

        this.telemetry.trackEvent({ name: fullEventName(eventName) }, props);
    }

    public postOperation(eventName: string, result: TelemetryResult, resultSummary?: string, extraProperties?: { [key: string]: string | number }): void {
        this.debugLog("postOperation", eventName, result, resultSummary, undefined, undefined);

        const props = {
            ...this.fillProperties(resultSummary, undefined, extraProperties),
            "DataModel.eventType": "Operation",
            // eslint-disable-next-line security/detect-object-injection
            "DataModel.result": TelemetryResult[result]
        }

        this.telemetry.trackEvent({ name: fullEventName(eventName) }, props);
    }

    private debugLog(action: string,
        eventName: string,
        result: TelemetryResult | null,
        details: string | undefined,
        feedbackId: string | undefined,
        extraProperties: { [key: string]: string } | undefined): void {
        // uncomment for debug logging
        if ((window as any).enableDebugLogging !== true) {
            return;
        }
        console.groupCollapsed(`${eventName.startsWith("deflect/") ? '🫸' + eventName : eventName} (${action})`)
        console.log(`(result: ${result}, details: '${details}', feedbackId: '${feedbackId}')`);
        if (extraProperties) {
            for (const key in extraProperties) {
                console.log(`%c${key}: ${extraProperties[key]}`, 'color: #2020aa');
            }
        }
        console.groupEnd();
    }

    private fillProperties(details?: string, feedbackId?: string, extraProperties?: { [key: string]: string | number }, isBusinessEvent: boolean = false): StringMap {
        const properties: StringMap = {};

        // nikolami: 2023-05-11: store.getState() will cause the following error:
        // "Unhandled Rejection (Error): You may not call store.getState() while the reducer is executing.
        // The reducer has already received the state as an argument. Pass it down from the top reducer instead of reading it from the store."
        // that's why we are using App.tempSignInState instead of App.store.getState()
        const signInState: SignInState = App.tempSignInState;
        const stateName: string = SignInState[signInState] ?? signInState.toString();
        properties[fullPropName("SignInState")] = stateName;
        properties[fullBIPropName("source_dialog")] = App.sourceDialog;

        if (isBusinessEvent) {
            properties[fullBIPropName("auth_state")] = stateName;
        }

        if (isBusinessEvent) {
            properties[fullBIPropName("feedbacksession")] = this.feedbackSessionId;
            properties[fullBIPropName("sessionstarttime")] = ` ${this.sessionStartTime}`; // workaround: added space since telemetry does not pick up the property value otherwise
        }

        if (details) {
            properties[fullPropName("Details")] = this.truncateString(details, 1023);
        }

        if (feedbackId) {
            properties[fullBIPropName("FeedbackId")] = feedbackId;
        }

        if (extraProperties) {
            // tslint:disable-next-line:forin
            for (const key in extraProperties) {
                // eslint-disable-next-line security/detect-object-injection
                const value = extraProperties[key];
                properties[fullPropName(key, isBusinessEvent)] = typeof value === "string" ? this.truncateString(value, 1023) : value;
            }
        }

        // uncomment for debugging
        // console.log("TELEMETRY PROPS");
        // for (const prop in properties) {
        //     console.log(`${prop}: ${properties[prop]}`)
        // }
        return properties;
    }

    // Telemetry only allows properties a length of 1024, so shorten if needed.
    private truncateString(input: string, maxLength: number): string {
        if (input.length > maxLength) {
            return input.substr(0, maxLength);
        }
        return input;
    }
}
